import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../lib/api';

type ApiLoginResponse = {
  message: string;
  emailSent?: boolean;
  devToken?: string;
};

type ApiVerifyResponse = {
  user: User;
  sessionToken: string;
};

interface User {
  id: string;
  email: string;
  name: string | null;
  role: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string) => Promise<ApiLoginResponse>;
  verifyToken: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post<ApiLoginResponse>('/auth/login', { email });
          const data = response.data || { message: 'Magic link sent' };
          set({ isLoading: false });
          return data;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Login failed';
          set({
            isLoading: false,
            error: errorMessage,
          });
          throw new Error(errorMessage);
        }
      },

      verifyToken: async (token: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post<ApiVerifyResponse>('/auth/verify', { token });
          const data = response.data;
          
          if (data && data.user && data.sessionToken) {
            set({
              user: data.user,
              token: data.sessionToken,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
          } else {
            throw new Error('Invalid response from server');
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Verification failed';
          set({
            isLoading: false,
            error: errorMessage,
            isAuthenticated: false,
          });
          throw new Error(errorMessage);
        }
      },

      logout: async () => {
        const { token } = get();
        set({ isLoading: true });
        
        if (token) {
          try {
            await api.post('/auth/logout', {}, {
              headers: { Authorization: `Bearer ${token}` },
            });
          } catch {
            // Ignore logout errors
          }
        }
        
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'mia-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
